/**
 * שירות API — הצעות מחיר מנו ספנות.
 * סוכן: /api/mano/* · ציבורי: /api/public/mano-quotes/* · אדמין: /api/admin/mano/*
 */
import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { buildAgentSessionRequestConfig } from "./agentApiSession";

async function agentRequest(method, path, { params, data, responseType } = {}) {
  const { headers, withCredentials, timeout } = await buildAgentSessionRequestConfig();
  const res = await axios({
    method,
    url: resolveApiUrl(path),
    params,
    data,
    headers,
    withCredentials,
    timeout: responseType === "blob" ? 60000 : timeout,
    responseType,
  });
  return res.data;
}

function extractApiError(error, fallback = "אירעה שגיאה — נסו שוב") {
  const data = error?.response?.data;
  if (data?.error?.message) return data.error.message;
  if (data?.message) return data.message;
  if (error?.message === "agent_no_session") return "נדרשת התחברות מחדש";
  return fallback;
}

// ============================ קטלוג (סוכן) ============================

export function listManoSailings(params = {}) {
  return agentRequest("get", "/api/mano/sailings", { params });
}

export function getManoSailing(sailingId) {
  return agentRequest("get", `/api/mano/sailings/${sailingId}`);
}

export function listManoCabinTypes() {
  return agentRequest("get", "/api/mano/cabin-types");
}

// ============================ הצעות (סוכן) ============================

export function listManoQuotes(params = {}) {
  return agentRequest("get", "/api/mano/quotes", { params });
}

export function createManoQuote(data) {
  return agentRequest("post", "/api/mano/quotes", { data });
}

export function getManoQuote(quoteId) {
  return agentRequest("get", `/api/mano/quotes/${quoteId}`);
}

export function updateManoQuote(quoteId, data) {
  return agentRequest("patch", `/api/mano/quotes/${quoteId}`, { data });
}

/**
 * Build a full PATCH body from a loaded quote (BE validate_quote_payload requires rooms etc.).
 * @param {object} quote
 * @param {{ final_total_price?: number|null }} [overrides]
 */
export function buildManoQuoteUpdatePayload(quote, overrides = {}) {
  const q = quote && typeof quote === "object" ? quote : {};
  const rooms = Array.isArray(q.rooms)
    ? q.rooms.map((r) => ({
        cabin_type_id: r.cabin_type_id,
        quantity: r.quantity || 1,
        adults: r.adults || 0,
        children_ages: r.children_ages_json || r.children_ages || [],
        infants_ages: r.infants_ages_json || r.infants_ages || [],
        accessible_requested: Boolean(r.accessible_requested),
        custom_cabin_name: r.custom_cabin_name || null,
        room_note: r.room_note || null,
        deck: r.deck != null && String(r.deck).trim() ? String(r.deck).trim() : null,
        room_price: r.room_price ?? null,
      }))
    : [];

  let validUntil = q.valid_until || null;
  if (typeof validUntil === "string" && validUntil.includes("T")) {
    validUntil = validUntil.slice(0, 10);
  }

  const finalTotal =
    overrides.final_total_price !== undefined
      ? overrides.final_total_price
      : q.final_total_price;

  return {
    sailing_id: q.sailing_id || q.sailing?.id,
    customer_full_name: q.customer_full_name || "",
    customer_phone: q.customer_phone || "",
    customer_email: q.customer_email || "",
    customer_display_name: q.customer_display_name || "",
    internal_note: q.internal_note || "",
    currency: "ILS",
    final_total_price: finalTotal,
    price_per_person: q.price_per_person ?? null,
    original_price: q.original_price ?? null,
    discount_amount: q.discount_amount ?? null,
    deposit_amount: q.deposit_amount ?? null,
    deposit_due_date: q.deposit_due_date ?? null,
    balance_due_date: q.balance_due_date ?? null,
    payment_terms: q.payment_terms || "",
    price_includes_handling_fee: Boolean(q.price_includes_handling_fee),
    price_includes_port_taxes: Boolean(q.price_includes_port_taxes),
    included_items: q.included_items_json ?? q.included_items ?? null,
    excluded_items: q.excluded_items_json ?? q.excluded_items ?? null,
    valid_until: validUntil,
    public_note: q.public_note || "",
    rooms,
  };
}

/** Admin quick-edit: update final_total_price on a closed deal (syncs linked cruise order). */
export async function updateManoClosedQuotePrice(quoteId, finalTotalPrice) {
  const amount = Number(finalTotalPrice);
  if (!Number.isFinite(amount) || amount <= 0) {
    const err = new Error("יש להזין סכום גדול מאפס");
    throw err;
  }
  const res = await getManoQuote(quoteId);
  const quote = res?.quote;
  if (!quote) {
    throw new Error("ההצעה לא נמצאה");
  }
  if (String(quote.status) !== "closed") {
    throw new Error("עריכת מחיר מהירה זמינה רק לעסקה שנסגרה");
  }
  return updateManoQuote(quoteId, buildManoQuoteUpdatePayload(quote, { final_total_price: amount }));
}

export function publishManoQuote(quoteId, { rotateLink = false } = {}) {
  return agentRequest("post", `/api/mano/quotes/${quoteId}/publish`, {
    data: { rotate_link: rotateLink },
  });
}

export function duplicateManoQuote(quoteId) {
  return agentRequest("post", `/api/mano/quotes/${quoteId}/duplicate`);
}

export function revokeManoQuoteLink(quoteId) {
  return agentRequest("post", `/api/mano/quotes/${quoteId}/revoke`);
}

export function archiveManoQuote(quoteId) {
  return agentRequest("post", `/api/mano/quotes/${quoteId}/archive`);
}

/**
 * Admin only: cancel a closed deal (archive quote + cancel linked cruise order).
 * @param {number|string} quoteId
 * @param {{ reason: string }} opts — cancellation reason (required by BE)
 */
export function cancelClosedManoQuote(quoteId, opts = {}) {
  const reason = String(opts.reason || "").trim();
  return agentRequest("post", `/api/mano/quotes/${quoteId}/cancel-closed`, {
    data: { reason },
  });
}

/** Update linked Mano cruise order_number (agent with access / admin). */
export function updateManoQuoteCruiseOrderNumber(quoteId, orderNumber) {
  return agentRequest("patch", `/api/mano/quotes/${quoteId}/cruise-order-number`, {
    data: { order_number: String(orderNumber || "").trim() },
  });
}

/**
 * Admin only: set Mano supplier ops stage on linked cruise order.
 * @param {number|string} quoteId
 * @param {string|null} status — payment_received | awaiting_mano_payment | paid_to_mano | null
 */
export function updateManoQuoteSupplierOpsStatus(quoteId, status) {
  return agentRequest("patch", `/api/mano/quotes/${quoteId}/supplier-ops-status`, {
    data: { status: status || null },
  });
}

/**
 * Close quote as נסגרה and promote matching CRM lead → docket.
 * @param {number|string} quoteId
 * @param {{ crmLeadId?: string }} [opts]
 */
export function closeManoQuote(quoteId, opts = {}) {
  const data = {};
  if (opts.crmLeadId) data.crm_lead_id = String(opts.crmLeadId);
  return agentRequest("post", `/api/mano/quotes/${quoteId}/close`, { data });
}

/** Find or create CRM docket for a Mano quote (Orders → מעבר לדוקט). */
export function ensureManoQuoteDocket(quoteId) {
  return agentRequest("post", `/api/mano/quotes/${quoteId}/ensure-docket`, { data: {} });
}

/**
 * Close quote (if needed), create cruise_orders row, ensure docket,
 * and return cruise_order_id for /p/dashboard/cruise-payment.
 */
export function prepareManoQuotePayment(quoteId, opts = {}) {
  const data = {};
  if (opts.crmLeadId) data.crm_lead_id = String(opts.crmLeadId);
  return agentRequest("post", `/api/mano/quotes/${quoteId}/prepare-payment`, { data });
}

export function recordManoQuoteEvent(quoteId, eventType) {
  return agentRequest("post", `/api/mano/quotes/${quoteId}/events`, {
    data: { event_type: eventType },
  }).catch(() => null); // תיעוד בלבד — כישלון שקט
}

export async function downloadManoQuotePdf(quoteId, quoteNumber) {
  const blob = await agentRequest("get", `/api/mano/quotes/${quoteId}/pdf`, {
    responseType: "blob",
  });
  triggerBlobDownload(blob, `HotelsRiver-Mano-Quote-${quoteNumber || quoteId}.pdf`);
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ============================ ציבורי ============================

export async function getPublicManoQuote(token) {
  const res = await axios.get(resolveApiUrl(`/api/public/mano-quotes/${token}`), {
    timeout: 20000,
  });
  return res.data;
}

export function publicManoQuotePdfUrl(token) {
  return resolveApiUrl(`/api/public/mano-quotes/${token}/pdf`);
}

export async function postPublicManoInterest(token, message) {
  const res = await axios.post(
    resolveApiUrl(`/api/public/mano-quotes/${token}/interest`),
    { message },
    { timeout: 20000 }
  );
  return res.data;
}

// ============================ אדמין ============================

export function adminManoSync(force = false) {
  return agentRequest("post", "/api/admin/mano/sync", { data: { force } });
}

export function adminManoSyncStatus() {
  return agentRequest("get", "/api/admin/mano/sync-status");
}

export function adminManoSyncLogs(limit = 50) {
  return agentRequest("get", "/api/admin/mano/sync-logs", { params: { limit } });
}

export async function adminManoImportFile(file, { deactivateMissing = false } = {}) {
  const { headers, withCredentials } = await buildAgentSessionRequestConfig();
  const form = new FormData();
  form.append("file", file);
  form.append("deactivate_missing", deactivateMissing ? "1" : "0");
  const res = await axios.post(resolveApiUrl("/api/admin/mano/import"), form, {
    headers: { ...headers, "Content-Type": "multipart/form-data" },
    withCredentials,
    timeout: 120000,
  });
  return res.data;
}

export function adminListManoSailings(params = {}) {
  return agentRequest("get", "/api/admin/mano/sailings", { params });
}

export function adminCreateManoSailing(data) {
  return agentRequest("post", "/api/admin/mano/sailings", { data });
}

export function adminUpdateManoSailing(sailingId, data) {
  return agentRequest("patch", `/api/admin/mano/sailings/${sailingId}`, { data });
}

export function adminListManoCabinTypes() {
  return agentRequest("get", "/api/admin/mano/cabin-types");
}

export function adminCreateManoCabinType(data) {
  return agentRequest("post", "/api/admin/mano/cabin-types", { data });
}

export function adminUpdateManoCabinType(cabinTypeId, data) {
  return agentRequest("patch", `/api/admin/mano/cabin-types/${cabinTypeId}`, { data });
}

export function adminGetManoSettings() {
  return agentRequest("get", "/api/admin/mano/settings");
}

export function adminUpdateManoSettings(data) {
  return agentRequest("patch", "/api/admin/mano/settings", { data });
}

export function adminListManoQuotes(params = {}) {
  return agentRequest("get", "/api/admin/mano/quotes", { params });
}

export { extractApiError as extractManoApiError };
